/**
 * Driver Applications Management Page
 * صفحة إدارة طلبات انضمام السائقين لشركة المياه
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTenantAuth } from '../../context/TenantAuthContext';
import { useToast } from '../../context/ToastContext';

/* ── Icons ─────────────────────────────────────────────────────── */
const I = {
  user:    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="8" r="4"/><path d="M20 21a8 8 0 1 0-16 0"/></svg>,
  check:   <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>,
  x:       <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  eye:     <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
  search:  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>,
  truck:   <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18h2a1 1 0 0 0 1-1v-3.28a1 1 0 0 0-.684-.948l-1.923-.641A1 1 0 0 1 15 12V8a1 1 0 0 1 1-1h1.382a1 1 0 0 1 .894.553l1.448 2.894A1 1 0 0 0 20.882 11H22v6a1 1 0 0 1-1 1h-1"/><circle cx="7" cy="18" r="2"/><circle cx="19" cy="18" r="2"/></svg>,
  drop:    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/></svg>,
  star:    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>,
  pause:   <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>,
  phone:   <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.27h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9a16 16 0 0 0 6 6l1.27-.85a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
  id:      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M16 10h-4m4 4h-4M8 10h.01M8 14h.01"/></svg>,
  back:    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>,
  doc:     <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>,
};

const WATER_LABELS = {
  drinking:     { label: 'مياه شرب',          bg: 'bg-blue-50 text-blue-700 border-blue-200' },
  non_drinking: { label: 'مياه غير صالحة للشرب', bg: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
  treated:      { label: 'مياه صرف معالجة',    bg: 'bg-green-50 text-green-700 border-green-200' },
};

const STATUS_MAP = {
  pending:   { label: 'قيد المراجعة', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  approved:  { label: 'مقبول',        cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  rejected:  { label: 'مرفوض',       cls: 'bg-red-50 text-red-700 border-red-200' },
  suspended: { label: 'موقوف',       cls: 'bg-gray-50 text-gray-600 border-gray-200' },
};

export default function DriverApplicationsPage() {
  const { apiCall } = useTenantAuth();
  const { success, error: showError } = useToast();

  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0, suspended: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [reviewModal, setReviewModal] = useState(null); // { app, action }
  const [rejectReason, setRejectReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [waterTypes, setWaterTypes] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (search) params.append('search', search);
      const [data, st] = await Promise.all([
        apiCall(`/tenant/driver-applications?${params}`),
        apiCall('/tenant/driver-applications/stats'),
      ]);
      setApps(data.items || []);
      setStats(st);
    } catch { showError('فشل تحميل الطلبات'); }
    finally { setLoading(false); }
  }, [apiCall, statusFilter, search, showError]);

  useEffect(() => { load(); }, [load]);

  const loadDetail = async (app) => {
    try {
      const d = await apiCall(`/tenant/driver-applications/${app.id}`);
      setSelected(d);
    } catch { showError('فشل تحميل التفاصيل'); }
  };

  const openReview = (app, action) => {
    setRejectReason('');
    setWaterTypes(app.water_types || []);
    setReviewModal({ app, action });
  };

  const submitReview = async () => {
    if (!reviewModal) return;
    const { app, action } = reviewModal;
    if (action === 'reject' && !rejectReason.trim()) {
      showError('يرجى ذكر سبب الرفض'); return;
    }
    setSubmitting(true);
    try {
      const res = await apiCall(`/tenant/driver-applications/${app.id}/review`, {
        method: 'POST',
        body: JSON.stringify({
          action,
          rejection_reason: action === 'reject' ? rejectReason : undefined,
          water_types: action === 'approve' ? waterTypes : undefined,
        }),
      });
      success(res.message);
      setReviewModal(null);
      setSelected(null);
      load();
    } catch (e) { showError(e.message); }
    finally { setSubmitting(false); }
  };

  const toggleWaterType = (wt) => {
    setWaterTypes(prev =>
      prev.includes(wt) ? prev.filter(w => w !== wt) : [...prev, wt]
    );
  };

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-xl font-bold text-gray-900">طلبات انضمام السائقين</h1>
          <p className="text-sm text-gray-500 mt-0.5">مراجعة وقبول أو رفض طلبات السائقين للانضمام</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-5">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { key: 'pending',   label: 'قيد المراجعة', color: 'text-amber-600',   bg: 'bg-amber-50',   border: 'border-amber-100' },
            { key: 'approved',  label: 'مقبولين',       color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-100' },
            { key: 'rejected',  label: 'مرفوضين',      color: 'text-red-600',     bg: 'bg-red-50',     border: 'border-red-100' },
            { key: 'suspended', label: 'موقوفين',      color: 'text-gray-600',    bg: 'bg-gray-50',    border: 'border-gray-100' },
          ].map(s => (
            <button key={s.key}
              onClick={() => setStatusFilter(statusFilter === s.key ? '' : s.key)}
              className={`rounded-xl p-4 border text-right transition-all ${s.bg} ${s.border} ${statusFilter === s.key ? 'ring-2 ring-offset-1 ring-blue-400' : 'hover:shadow-sm'}`}>
              <p className={`text-2xl font-black ${s.color}`}>{stats[s.key]}</p>
              <p className="text-xs text-gray-500 mt-1">{s.label}</p>
            </button>
          ))}
        </div>

        {/* Search + filter */}
        <div className="flex gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">{I.search}</span>
            <input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="ابحث بالاسم أو الجوال أو الهوية..."
              className="w-full pr-9 pl-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white"
            />
          </div>
          {statusFilter && (
            <button onClick={() => setStatusFilter('')}
              className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-white border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">
              {I.x} مسح الفلتر
            </button>
          )}
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400">
            <div className="w-7 h-7 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin"/>
          </div>
        ) : apps.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 flex flex-col items-center py-20 text-gray-400">
            <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-3 text-gray-300">{I.user}</div>
            <p className="text-sm font-bold text-gray-500">لا توجد طلبات</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-xs text-gray-500 border-b border-gray-100">
                    <th className="py-3 px-4 text-right font-semibold">السائق</th>
                    <th className="py-3 px-4 text-right font-semibold">أنواع المياه</th>
                    <th className="py-3 px-4 text-right font-semibold">المركبة</th>
                    <th className="py-3 px-4 text-right font-semibold">الحالة</th>
                    <th className="py-3 px-4 text-right font-semibold">التاريخ</th>
                    <th className="py-3 px-4 text-right font-semibold">الإجراءات</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {apps.map(app => {
                    const st = STATUS_MAP[app.status] || STATUS_MAP.pending;
                    return (
                      <tr key={app.id} className="hover:bg-gray-50/50 transition-colors">
                        {/* Driver */}
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0 text-blue-600">
                              {app.driver_photo
                                ? <img src={app.driver_photo} alt="" className="w-full h-full rounded-xl object-cover"/>
                                : <span className="text-sm font-bold">{app.driver_name?.[0] || '?'}</span>}
                            </div>
                            <div>
                              <p className="font-bold text-gray-900">{app.driver_name}</p>
                              <p className="text-[11px] text-gray-400 flex items-center gap-1 mt-0.5">
                                {I.phone} {app.driver_phone}
                              </p>
                            </div>
                          </div>
                        </td>
                        {/* Water types */}
                        <td className="py-3 px-4">
                          <div className="flex flex-wrap gap-1">
                            {(app.water_types || []).map(wt => {
                              const w = WATER_LABELS[wt];
                              return w ? (
                                <span key={wt} className={`px-2 py-0.5 rounded-md text-[11px] font-bold border ${w.bg}`}>{w.label}</span>
                              ) : null;
                            })}
                          </div>
                        </td>
                        {/* Vehicle */}
                        <td className="py-3 px-4">
                          <span className="text-xs text-gray-500">{app.vehicle_info || '—'}</span>
                        </td>
                        {/* Status */}
                        <td className="py-3 px-4">
                          <span className={`px-2.5 py-1 rounded-lg text-[11px] font-bold border ${st.cls}`}>{st.label}</span>
                        </td>
                        {/* Date */}
                        <td className="py-3 px-4 text-xs text-gray-400">
                          {app.created_at ? new Date(app.created_at).toLocaleDateString('ar-SA') : '—'}
                        </td>
                        {/* Actions */}
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-1.5">
                            <button onClick={() => loadDetail(app)}
                              className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-blue-50 text-gray-400 hover:text-blue-500 transition-colors">
                              {I.eye}
                            </button>
                            {app.status === 'pending' && <>
                              <button onClick={() => openReview(app, 'approve')}
                                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-emerald-50 text-gray-400 hover:text-emerald-600 transition-colors">
                                {I.check}
                              </button>
                              <button onClick={() => openReview(app, 'reject')}
                                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors">
                                {I.x}
                              </button>
                            </>}
                            {app.status === 'approved' && (
                              <button onClick={() => openReview(app, 'suspend')}
                                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
                                {I.pause}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Detail Drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex" dir="rtl">
          <div className="absolute inset-0 bg-black/30" onClick={() => setSelected(null)}/>
          <div className="absolute left-0 top-0 bottom-0 w-full max-w-md bg-white shadow-2xl overflow-y-auto flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h2 className="font-bold text-gray-900">تفاصيل الطلب</h2>
              <button onClick={() => setSelected(null)} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400">{I.x}</button>
            </div>
            <div className="flex-1 p-5 space-y-5">
              {/* Driver info */}
              <div className="bg-gray-50 rounded-xl p-4 flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-blue-100 flex items-center justify-center text-blue-600 text-xl font-black">
                  {selected.driver_photo
                    ? <img src={selected.driver_photo} alt="" className="w-full h-full rounded-2xl object-cover"/>
                    : selected.driver_name?.[0]}
                </div>
                <div className="flex-1">
                  <p className="font-black text-gray-900 text-base">{selected.driver_name}</p>
                  <div className="flex flex-wrap gap-3 mt-1 text-xs text-gray-500">
                    <span className="flex items-center gap-1">{I.phone} {selected.driver_phone}</span>
                    <span className="flex items-center gap-1">{I.id} {selected.driver_national_id || '—'}</span>
                  </div>
                  {selected.driver_rating > 0 && (
                    <div className="flex items-center gap-1 mt-1 text-amber-500 text-xs">
                      {I.star} {selected.driver_rating.toFixed(1)} ({selected.driver_orders} طلب)
                    </div>
                  )}
                </div>
                <span className={`px-2.5 py-1 rounded-lg text-[11px] font-bold border ${(STATUS_MAP[selected.status] || STATUS_MAP.pending).cls}`}>
                  {(STATUS_MAP[selected.status] || STATUS_MAP.pending).label}
                </span>
              </div>

              {/* Water types */}
              <div>
                <p className="text-xs font-bold text-gray-500 mb-2">أنواع المياه المطلوبة</p>
                <div className="flex flex-wrap gap-2">
                  {(selected.water_types || []).map(wt => {
                    const w = WATER_LABELS[wt];
                    return w ? <span key={wt} className={`px-3 py-1 rounded-lg text-xs font-bold border ${w.bg}`}>{w.label}</span> : null;
                  })}
                  {(!selected.water_types || selected.water_types.length === 0) && <span className="text-xs text-gray-400">لم يحدد</span>}
                </div>
              </div>

              {/* Vehicle */}
              {selected.vehicle_info && (
                <div className="bg-blue-50 rounded-xl p-3 flex items-center gap-2 text-sm text-blue-800">
                  {I.truck} <span className="font-bold">{selected.vehicle_info}</span>
                </div>
              )}

              {/* Notes */}
              {selected.notes && (
                <div className="bg-amber-50 rounded-xl p-3 text-sm text-amber-800">
                  <p className="font-bold text-xs mb-1">ملاحظة السائق</p>
                  {selected.notes}
                </div>
              )}

              {/* Rejection reason */}
              {selected.rejection_reason && (
                <div className="bg-red-50 rounded-xl p-3 text-sm text-red-700">
                  <p className="font-bold text-xs mb-1">سبب الرفض</p>
                  {selected.rejection_reason}
                </div>
              )}

              {/* Requirements */}
              {selected.requirements && selected.requirements.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-gray-500 mb-3 flex items-center gap-1.5">{I.doc} المستندات والمتطلبات</p>
                  <div className="space-y-2">
                    {selected.requirements.map(r => (
                      <div key={r.field_key} className={`flex items-center justify-between px-3 py-2.5 rounded-lg border text-sm
                        ${r.verification_status === 'approved' ? 'bg-emerald-50 border-emerald-100' :
                          r.verification_status === 'missing' ? 'bg-gray-50 border-dashed border-gray-200' :
                          'bg-white border-gray-100'}`}>
                        <span className="text-gray-700">{r.field_name_ar}</span>
                        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-md
                          ${r.verification_status === 'approved' ? 'text-emerald-600 bg-emerald-100' :
                            r.verification_status === 'rejected' ? 'text-red-600 bg-red-100' :
                            r.verification_status === 'missing'  ? 'text-gray-400 bg-gray-100' :
                            'text-amber-600 bg-amber-100'}`}>
                          {r.verification_status === 'approved' ? 'مكتمل' :
                           r.verification_status === 'rejected'  ? 'مرفوض' :
                           r.verification_status === 'missing'   ? 'غير مرفوع' : 'قيد المراجعة'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Action buttons */}
            {selected.status === 'pending' && (
              <div className="px-5 py-4 border-t border-gray-100 flex gap-2">
                <button onClick={() => { setSelected(null); openReview(selected, 'approve'); }}
                  className="flex-1 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold transition-colors flex items-center justify-center gap-2">
                  {I.check} قبول
                </button>
                <button onClick={() => { setSelected(null); openReview(selected, 'reject'); }}
                  className="flex-1 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-bold transition-colors flex items-center justify-center gap-2">
                  {I.x} رفض
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Review Modal */}
      {reviewModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" dir="rtl">
          <div className="absolute inset-0 bg-black/40" onClick={() => setReviewModal(null)}/>
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-5">
            <h3 className={`text-lg font-black ${reviewModal.action === 'approve' ? 'text-emerald-700' : reviewModal.action === 'reject' ? 'text-red-700' : 'text-gray-700'}`}>
              {reviewModal.action === 'approve' ? '✓ قبول الطلب' :
               reviewModal.action === 'reject'  ? '✕ رفض الطلب' : '⏸ تعليق السائق'}
            </h3>
            <p className="text-sm text-gray-600">
              {reviewModal.action === 'approve'
                ? `سيتم قبول "${reviewModal.app.driver_name}" وإضافته لقائمة سائقيك.`
                : reviewModal.action === 'reject'
                ? `سيتم رفض طلب "${reviewModal.app.driver_name}" مع إشعاره بالسبب.`
                : `سيتم تعليق "${reviewModal.app.driver_name}" مؤقتاً.`}
            </p>

            {/* Water types on approve */}
            {reviewModal.action === 'approve' && (
              <div>
                <p className="text-xs font-bold text-gray-600 mb-2">أنواع المياه المصرّح بها</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(WATER_LABELS).map(([key, w]) => (
                    <button key={key} onClick={() => toggleWaterType(key)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${
                        waterTypes.includes(key) ? w.bg + ' shadow-sm' : 'bg-white border-gray-200 text-gray-400'}`}>
                      {w.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Reject reason */}
            {reviewModal.action === 'reject' && (
              <div>
                <label className="text-xs font-bold text-gray-600 mb-1.5 block">سبب الرفض *</label>
                <textarea
                  value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                  rows={3} placeholder="اذكر السبب ليتمكن السائق من التصحيح..."
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-300"
                />
              </div>
            )}

            <div className="flex gap-2 pt-1">
              <button onClick={submitReview} disabled={submitting}
                className={`flex-1 py-2.5 rounded-xl text-white text-sm font-bold transition-colors flex items-center justify-center gap-2 ${
                  reviewModal.action === 'approve' ? 'bg-emerald-500 hover:bg-emerald-600' :
                  reviewModal.action === 'reject'  ? 'bg-red-500 hover:bg-red-600' :
                  'bg-gray-500 hover:bg-gray-600'} ${submitting ? 'opacity-60' : ''}`}>
                {submitting ? <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"/> : null}
                {reviewModal.action === 'approve' ? 'تأكيد القبول' :
                 reviewModal.action === 'reject'  ? 'تأكيد الرفض' : 'تأكيد التعليق'}
              </button>
              <button onClick={() => setReviewModal(null)} className="px-5 py-2.5 rounded-xl bg-gray-100 text-gray-600 text-sm font-medium hover:bg-gray-200 transition-colors">
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
